import React, { useState, useRef, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18n';
import { isEmpty, findIndex, size, isFunction } from 'lodash';
import withKeepingLastFocus from 'components/HOCs/withKeepingLastFocus';
import SpatialNavigationWrapper from 'components/navigation/SpatialNavigationWrapper';
import Logo from 'components/Logo';
import Clock from 'components/Clock';
import CollectionList from './CollectionList';
import {
  StyledEmptyDataStub,
  StyledCollection,
  StyledCollectionWrapper,
  StyledCollectionTitle,
} from './StyledComponents';

const SCROLL_SIZE = 795;
const LAST_FOCUS_KEY = 'SECTION_COLLECTION';

const getFocusBackConfig = (listId, elementIndex) => ({
  sectionToFocus: `@${listId}`,
  elementToFocus: `#${listId}-item-${elementIndex}`,
});

const CustomCollection = (props) => {
  const {
    top,
    left,
    small,
    showLogo,
    showClock,
    onContentEnter,
    loadExtraItems,
    onContentFocus,
    className,
  } = props;
  const { title, collections, startListScrollIndex } = props;
  const { focusedCollectionId, elementIndex, lastFocusKeeper } = props;
  const lastFocus = lastFocusKeeper.getSaved(LAST_FOCUS_KEY) || {};
  const [focusedListIndex, setFocusedListIndex] = useState(() => {
    // todo: comment
    if (Number.isInteger(lastFocus.listIndex)) return lastFocus.listIndex;
    const i = findIndex(collections, { id: focusedCollectionId });
    return i !== -1 ? i : 0;
  });

  const collectionsRef = useRef(collections);

  const offset = focusedListIndex * SCROLL_SIZE;

  const onEnter = useCallback(
    (item, index, list) => {
      const focusBackConfig = getFocusBackConfig(list.id, index);
      const lastFocusConfig = { listIndex: list.index, elementIndex: index };
      onContentEnter(item, index, focusBackConfig);
      lastFocusKeeper.save(LAST_FOCUS_KEY, lastFocusConfig);
    },
    [onContentEnter, lastFocusKeeper],
  );

  const onFocus = useCallback(
    (item, index, list) => {
      if (isFunction(loadExtraItems)) {
        const { id, content } = collections[list.index];
        const listSize = size(content);
        const isRequiresExtraLoad = index >= listSize - 1 - 4;
        if (isRequiresExtraLoad) loadExtraItems(id);
      }

      if (onContentFocus) {
        onContentFocus(item, index);
      }

      setFocusedListIndex(list.index);
    },
    [collections, loadExtraItems, onContentFocus],
  );

  // TODO: solve bug with focus
  useEffect(() => {
    if (collectionsRef.current.length !== collections.length) {
      const focusedListId = collectionsRef.current[focusedListIndex].id;
      const index = findIndex(collections, { id: focusedListId });
      setFocusedListIndex(index);
      setTimeout(() => {
        SpatialNavigationWrapper.focus(`@collection-list-${focusedListId}`);
      }, 0);
    }
    collectionsRef.current = collections;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collections]);
  // Clear last focus after "mount"
  useEffect(() => {
    if (!lastFocusKeeper.hasSaved(LAST_FOCUS_KEY)) return;
    // async timeout necessary here
    setTimeout(() => lastFocusKeeper.reset(LAST_FOCUS_KEY), 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {showLogo && (
        <Logo hide={focusedListIndex > 0} noAnimation>
          <Logo.HeroGo />
        </Logo>
      )}
      {showClock && <Clock hide={focusedListIndex > 0} noAnimation />}
      <StyledCollection
        small={small}
        top={top}
        left={left}
        className={className}
        sectionName="CUSTOM_COLLECTION"
      >
        {title && <StyledCollectionTitle offset={offset}>{title}</StyledCollectionTitle>}
        <StyledCollectionWrapper offset={offset}>
          {collections.map((collection, index) => {
            const { data, contentType, title, id } = collection;
            const listId = `collection-list-${id}`;
            const defaultSection = index === focusedListIndex;
            // Default index should be taken from lastFocus
            // if current list is last focused list after navigation back
            let defaultIndex = lastFocus.listIndex === index ? lastFocus.elementIndex : 0;

            if (defaultSection && elementIndex >= 0) defaultIndex = elementIndex;

            const prevItem = collections[index - 1];
            const nextItem = collections[index + 1];
            // Fixes navigation probles because of sub tabs under lists
            const config = {
              leaveFor: {
                up: prevItem ? `@collection-list-${prevItem.id}` : 'default',
                down: nextItem ? `@collection-list-${nextItem.id}` : 'default',
                right: '',
              },
            };

            return (
              <CollectionList
                key={listId}
                id={listId}
                title={title}
                index={index}
                defaultIndex={defaultIndex}
                defaultSection={defaultSection}
                startScrollIndex={startListScrollIndex}
                items={data}
                contentType={contentType}
                onFocus={onFocus}
                onEnter={onEnter}
                config={config}
              />
            );
          })}
        </StyledCollectionWrapper>
      </StyledCollection>
    </>
  );
};

const collectionShape = PropTypes.shape({
  data: PropTypes.array.isRequired,
  contentType: PropTypes.string,
  title: PropTypes.string,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
});

CustomCollection.propTypes = {
  title: PropTypes.string,
  collections: PropTypes.arrayOf(collectionShape).isRequired,
  startListScrollIndex: PropTypes.number,
  focusedCollectionId: PropTypes.string,
  top: PropTypes.number,
  left: PropTypes.number,
  small: PropTypes.bool,
  showLogo: PropTypes.bool,
  showClock: PropTypes.bool,
  onContentEnter: PropTypes.func.isRequired,
  loadExtraItems: PropTypes.func,
  lastFocusKeeper: PropTypes.object,
  className: PropTypes.string,
};

const CollectionContainer = (props) => {
  const { isLoading, collections } = props;
  // "isLoading" could be "undefined" before loading starts
  if (isLoading === false && isEmpty(collections)) {
    return <StyledEmptyDataStub>{i18n.t('common.nothingFound')}</StyledEmptyDataStub>;
  } else if (isEmpty(collections)) {
    return null;
  }
  return <CustomCollection {...props} />;
};

export default withKeepingLastFocus(CollectionContainer);
